import React, { useEffect, useState } from "react";
import { FaUsers, FaHeartbeat, FaNotesMedical, FaCheckCircle, FaClinicMedical } from "react-icons/fa";

/**
 * NCD KPI Cards component (Argon style)
 *
 * Usage:
 * <NcdKpiCards apiUrl="/api/ncd/kpis" />
 *
 * Props:
 *  - apiUrl (string) : endpoint returning JSON object with keys:
 *      total_screened, new_htn, new_dm, controlled_htn_pct, controlled_dm_pct, reporting_facilities
 *  - pollInterval (number, ms) : optional auto-refresh interval
 *
 * Notes:
 *  - This component uses Argon-like card markup (Bootstrap-ish) and minimal Tailwind utilities.
 *  - It gracefully falls back to a small local mock when apiUrl is not provided or the fetch fails.
 */

export default function NcdKpiCards({ apiUrl = '/api/ncd/kpis', pollInterval = null }) {
	const [kpis, setKpis] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	const fetchKpis = async () => {
		setLoading(true);
		setError(null);
		try {
			if (!apiUrl) throw new Error('No API URL provided');
			const res = await fetch(apiUrl, { cache: 'no-store' });
			if (!res.ok) throw new Error(`Fetch error: ${res.status}`);
			const json = await res.json();
			// Expecting numbers or strings; normalize here
			setKpis({
				total_screened: json.total_screened ?? json.totalScreened ?? 0,
				new_htn: json.new_htn ?? json.newHTN ?? 0,
				new_dm: json.new_dm ?? json.newDM ?? 0,
				controlled_htn_pct: json.controlled_htn_pct ?? json.controlledHTNPercent ?? 0,
				controlled_dm_pct: json.controlled_dm_pct ?? json.controlledDMPercent ?? 0,
				reporting_facilities: json.reporting_facilities ?? json.reportingFacilities ?? 0,
			});
		} catch (err) {
			console.error(err);
			setError(err.message || 'Failed to load KPIs');
			// fallback mock data so the UI still shows something useful
			setKpis({
				total_screened: 12450,
				new_htn: 320,
				new_dm: 210,
				controlled_htn_pct: 68,
				controlled_dm_pct: 61,
				reporting_facilities: 142,
			});
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchKpis();
		if (pollInterval) {
			const id = setInterval(fetchKpis, pollInterval);
			return () => clearInterval(id);
		}
	}, [apiUrl, pollInterval]);

	const Card = ({ icon, title, value, hint, colorClass = 'text-indigo-600' }) => (
		<div className="card shadow-sm border-0">
			<div className="card-body d-flex align-items-center">
				<div className="me-2 rounded-circle p-3 bg-light" style={{ minWidth: 58, minHeight: 58 }}>
					<div className={"d-flex align-items-center justify-content-center " + colorClass} style={{ fontSize: 22 }}>
						{icon}
					</div>
				</div>
				<div className="flex-grow-1">
					<div className="text-sm text-muted">{title}</div>
					<div className="h4 mb-0 mt-1">{value}</div>
					{hint && <div className="text-xs text-muted mt-1">{hint}</div>}
				</div>
			</div>
		</div>
	);

	return (
		<section>
			{/* <div className="d-flex align-items-center justify-content-between mb-3">
        <h4 className="m-0">NCD — KPI Summary</h4>
        <div className="text-muted small">Last updated: {loading ? 'Loading...' : new Date().toLocaleString()}</div>
      </div>

      {error && (
        <div className="alert alert-warning py-2">Failed to fetch live KPIs — showing cached/mock values. ({error})</div>
      )} */}

			<div className="row g-2">
				<div className="col-12 col-md-2">
					<Card
						icon={<FaUsers />}
						title="Total Beneficiaries Screened"
						value={loading ? '—' : kpis?.total_screened?.toLocaleString() ?? '—'}
						// hint="Number of individuals screened for NCDs"
						colorClass="text-primary"
					/>
				</div>

				<div className="col-12 col-md-2">
					<Card
						icon={<FaHeartbeat />}
						title="New HTN Cases Detected"
						value={loading ? '—' : kpis?.new_htn ?? '—'}
						// hint="Monthly new hypertension cases"
						colorClass="text-danger"
					/>
				</div>

				<div className="col-12 col-md-2">
					<Card className="pl-1"
						icon={<FaNotesMedical />}
						title="New Diabetes Cases Detected"
						value={loading ? '—' : kpis?.new_dm ?? '—'}
						// hint="Monthly new diabetes cases"
						colorClass="text-warning"
					/>
				</div>

				<div className="col-12 col-md-2">
					<Card
						icon={<FaCheckCircle />}
						title="Controlled HTN Cases (%)"
						value={loading ? '—' : `${kpis?.controlled_htn_pct ?? '—'}%`}
						// hint="% of HTN patients under control (target ≥ 70%)"
						colorClass={kpis && kpis.controlled_htn_pct >= 70 ? 'text-success' : 'text-danger'}
					/>
				</div>

				<div className="col-12 col-md-2">
					<Card
						icon={<FaCheckCircle />}
						title="Controlled DM Cases (%)"
						value={loading ? '—' : `${kpis?.controlled_dm_pct ?? '—'}%`}
						// hint="% of DM patients under control (target ≥ 60%)"
						colorClass={kpis && kpis.controlled_dm_pct >= 60 ? 'text-success' : 'text-danger'}
					/>
				</div>

				<div className="col-12 col-md-2">
					<Card
						icon={<FaClinicMedical />}
						title="Reporting Facilities"
						value={loading ? '—' : kpis?.reporting_facilities ?? '—'}
						// hint="Facilities submitted NCD data"
						colorClass="text-info"
					/>
				</div>
			</div>
		</section>
	);
}
